import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import type { ReactNode } from 'react';
import { Button } from '../../../../components-v2/Button';
import { Checkbox } from '../../../../components-v2/Checkbox';
import { Field, FieldLabel } from '../../../../components-v2/Field';
import { Input } from '../../../../components-v2/Input';
import { THEME } from '../../../../components-v2/Theme/theme';
import { CloseButton } from '../../../../components/CloseButton';
import { Modal } from '../../../../components/Modal';
import { MultiEmployeeSelector } from '../../../../components/email/MultiEmployeeSelector';
import { styles } from './styles';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';

type Props = {
	isOpen: boolean;
	onClose: () => void;
	editingGroup?: Api.IUserGroup | null;
	onGroupNameChange: (name: string) => void;
	onAutomationPermissionChange: (checked: boolean, permission: 'read' | 'write' | 'execute' | 'viewReporting') => void;
	onHandwrittenCardsPermissionChange: (
		checked: boolean,
		permission: 'read' | 'write' | 'send' | 'viewReporting'
	) => void;
	onAiPublicConversationPermissionChange: (checked: boolean, permission: 'read' | 'write' | 'viewReporting') => void;
	onSchedulerPermissionChange: (checked: boolean, permission: 'read' | 'write' | 'viewReporting') => void;
	showMeetingsPermission: boolean;
	onSurveyPermissionChange: (
		checked: boolean,
		permission: 'read' | 'write' | 'viewReporting',
		surveyType?: 'eventRegistrationSurvey' | 'satisfactionSurvey' | 'customSurvey' | 'donationSurvey'
	) => void;
	onContentLibraryPermissionChange: (
		checked: boolean,
		template: 'viewEmailTemplates' | 'viewSocialTemplates' | 'viewCardTemplates' | 'viewHtmlNewsletterTemplates'
	) => void;
	onSocialMediaPermissionChange: (checked: boolean, permission: 'read' | 'write' | 'send' | 'viewReporting') => void;
	onFilesPermissionChange: (
		checked: boolean,
		permission: 'read' | 'write' | 'viewReporting' | 'sendDownloadLink' | 'requestFile' | 'delete'
	) => void;
	selectedUsers: Api.IUser[];
	onUserSelectionChange: (users: Api.IUser[]) => void;
	onSaveGroup: () => void;
	isPending: boolean;
};

const PermissionCheckbox = ({
	id,
	label,
	checked,
	onCheckedChange,
}: {
	id: string;
	label: string;
	checked: boolean;
	onCheckedChange: (checked: boolean) => void;
}) => {
	return (
		<div className={css(styles.permissionCheckboxItem)}>
			<Checkbox id={id} checked={checked} onCheckedChange={value => onCheckedChange(Boolean(value))} />
			<label htmlFor={id} className={css(styles.permissionCheckboxLabel)}>
				{label}
			</label>
		</div>
	);
};

const FeatureSection = ({ title, children }: { title: string; children: ReactNode }) => {
	return (
		<div className={css(styles.featureSection)}>
			<div className={css(styles.featureSectionTitle)}>{title}</div>
			{children}
		</div>
	);
};

const SURVEY_TYPES: {
	key: 'eventRegistrationSurvey' | 'satisfactionSurvey' | 'customSurvey' | 'donationSurvey';
	label: string;
}[] = [
	{ key: 'eventRegistrationSurvey', label: 'Event Registration' },
	{ key: 'satisfactionSurvey', label: 'Satisfaction' },
	{ key: 'customSurvey', label: 'Custom' },
	{ key: 'donationSurvey', label: 'Donation' },
];

export const CreateUserGroupModal = ({
	isOpen,
	onClose,
	editingGroup,
	onGroupNameChange,
	onAutomationPermissionChange,
	onHandwrittenCardsPermissionChange,
	onAiPublicConversationPermissionChange,
	onSchedulerPermissionChange,
	showMeetingsPermission,
	onSurveyPermissionChange,
	onContentLibraryPermissionChange,
	onSocialMediaPermissionChange,
	onFilesPermissionChange,
	onUserSelectionChange,
	onSaveGroup,
	selectedUsers,
	isPending,
}: Props) => {
	const userSession = useUserSession();

	return (
		<Modal isOpen={isOpen} onRequestClose={onClose} shouldCloseOnOverlayClick={false}>
			<div className={css(styles.modal)}>
				<div className={css(styles.modalHeaderCloseButton)}>
					<CloseButton onClick={onClose} size={12} fillColor={THEME.colors.text.light} />
				</div>

				<div className={css(styles.modalContainer)}>
					<div className={css(styles.modalHeader)}>
						<h2 className={css(styles.modalTitle)}>{editingGroup?.id ? 'Edit User Group' : 'Create User Group'}</h2>
					</div>

					{editingGroup && (
						<>
							<div className={css(styles.formGroup)}>
								<Field>
									<FieldLabel htmlFor='user-group-name'>
										Group Name
										{editingGroup.isPredefined && (
											<span className={css(styles.builtInLabel)}>(Built-in - cannot be renamed)</span>
										)}
									</FieldLabel>
									<Input
										id='user-group-name'
										type='text'
										value={editingGroup.name}
										onChange={e => onGroupNameChange(e.target.value)}
										placeholder='Enter group name'
										disabled={editingGroup.isPredefined}
									/>
								</Field>
							</div>

							<FeatureSection title='Automations'>
								<div className={css(styles.permissionCheckboxGroup)}>
									<PermissionCheckbox
										id='automation-read'
										label='Read'
										checked={editingGroup.featurePermissions?.automation?.read || false}
										onCheckedChange={checked => onAutomationPermissionChange(checked, 'read')}
									/>
									<PermissionCheckbox
										id='automation-write'
										label='Write'
										checked={editingGroup.featurePermissions?.automation?.write || false}
										onCheckedChange={checked => onAutomationPermissionChange(checked, 'write')}
									/>
									<PermissionCheckbox
										id='automation-execute'
										label='Execute'
										checked={editingGroup.featurePermissions?.automation?.execute || false}
										onCheckedChange={checked => onAutomationPermissionChange(checked, 'execute')}
									/>
									<PermissionCheckbox
										id='automation-viewReporting'
										label='View Reporting'
										checked={editingGroup.featurePermissions?.automation?.viewReporting || false}
										onCheckedChange={checked => onAutomationPermissionChange(checked, 'viewReporting')}
									/>
								</div>
							</FeatureSection>

							{userSession.account.features?.handwrittenCards?.enabled && (
								<FeatureSection title='Cards'>
									<div className={css(styles.permissionCheckboxGroup)}>
										<PermissionCheckbox
											id='card-read'
											label='Read'
											checked={editingGroup.featurePermissions?.handwrittenCard?.read || false}
											onCheckedChange={checked => onHandwrittenCardsPermissionChange(checked, 'read')}
										/>
										<PermissionCheckbox
											id='card-write'
											label='Write'
											checked={editingGroup.featurePermissions?.handwrittenCard?.write || false}
											onCheckedChange={checked => onHandwrittenCardsPermissionChange(checked, 'write')}
										/>
										<PermissionCheckbox
											id='card-send'
											label='Send'
											checked={editingGroup.featurePermissions?.handwrittenCard?.send || false}
											onCheckedChange={checked => onHandwrittenCardsPermissionChange(checked, 'send')}
										/>
										<PermissionCheckbox
											id='card-viewReporting'
											label='View Reporting'
											checked={editingGroup.featurePermissions?.handwrittenCard?.viewReporting || false}
											onCheckedChange={checked => onHandwrittenCardsPermissionChange(checked, 'viewReporting')}
										/>
									</div>
								</FeatureSection>
							)}

							{userSession.account.features?.socialMedia?.enabled && (
								<FeatureSection title='Social Media'>
									<div className={css(styles.permissionCheckboxGroup)}>
										<PermissionCheckbox
											id='social-media-read'
											label='Read'
											checked={editingGroup.featurePermissions?.socialMedia?.read || false}
											onCheckedChange={checked => onSocialMediaPermissionChange(checked, 'read')}
										/>
										<PermissionCheckbox
											id='social-media-write'
											label='Write'
											checked={editingGroup.featurePermissions?.socialMedia?.write || false}
											onCheckedChange={checked => onSocialMediaPermissionChange(checked, 'write')}
										/>
										<PermissionCheckbox
											id='social-media-post'
											label='Post'
											checked={editingGroup.featurePermissions?.socialMedia?.send || false}
											onCheckedChange={checked => onSocialMediaPermissionChange(checked, 'send')}
										/>
										<PermissionCheckbox
											id='social-media-viewReporting'
											label='View Reporting'
											checked={editingGroup.featurePermissions?.socialMedia?.viewReporting || false}
											onCheckedChange={checked => onSocialMediaPermissionChange(checked, 'viewReporting')}
										/>
									</div>
								</FeatureSection>
							)}

							{userSession.account.features?.files?.enabled && (
								<FeatureSection title='Files'>
									<div className={css(styles.permissionCheckboxGroup)}>
										<PermissionCheckbox
											id='files-read'
											label='View'
											checked={editingGroup.featurePermissions?.files?.read || false}
											onCheckedChange={checked => onFilesPermissionChange(checked, 'read')}
										/>
										<PermissionCheckbox
											id='files-send-download-link'
											label='Send Download Link'
											checked={editingGroup.featurePermissions?.files?.sendDownloadLink || false}
											onCheckedChange={checked => onFilesPermissionChange(checked, 'sendDownloadLink')}
										/>
										<PermissionCheckbox
											id='files-request-file'
											label='Request File'
											checked={editingGroup.featurePermissions?.files?.requestFile || false}
											onCheckedChange={checked => onFilesPermissionChange(checked, 'requestFile')}
										/>
										<PermissionCheckbox
											id='files-delete'
											label='Delete'
											checked={editingGroup.featurePermissions?.files?.delete || false}
											onCheckedChange={checked => onFilesPermissionChange(checked, 'delete')}
										/>
										<PermissionCheckbox
											id='files-viewReporting'
											label='View Reporting'
											checked={editingGroup.featurePermissions?.files?.viewReporting || false}
											onCheckedChange={checked => onFilesPermissionChange(checked, 'viewReporting')}
										/>
									</div>
								</FeatureSection>
							)}

							<FeatureSection title='AI Conversations'>
								<div className={css(styles.permissionCheckboxGroup)}>
									<PermissionCheckbox
										id='ai-conversation-read'
										label='Read'
										checked={editingGroup.featurePermissions?.aiPublicConversation?.read || false}
										onCheckedChange={checked => onAiPublicConversationPermissionChange(checked, 'read')}
									/>
									<PermissionCheckbox
										id='ai-conversation-write'
										label='Write'
										checked={editingGroup.featurePermissions?.aiPublicConversation?.write || false}
										onCheckedChange={checked => onAiPublicConversationPermissionChange(checked, 'write')}
									/>
									<PermissionCheckbox
										id='ai-conversation-viewReporting'
										label='View Reporting'
										checked={editingGroup.featurePermissions?.aiPublicConversation?.viewReporting || false}
										onCheckedChange={checked => onAiPublicConversationPermissionChange(checked, 'viewReporting')}
									/>
								</div>
							</FeatureSection>

							{showMeetingsPermission ? (
								<FeatureSection title='Meetings'>
									<div className={css(styles.permissionCheckboxGroup)}>
										<PermissionCheckbox
											id='scheduler-read'
											label='Read'
											checked={editingGroup.featurePermissions?.scheduler?.read || false}
											onCheckedChange={checked => onSchedulerPermissionChange(checked, 'read')}
										/>
										<PermissionCheckbox
											id='scheduler-write'
											label='Write'
											checked={editingGroup.featurePermissions?.scheduler?.write || false}
											onCheckedChange={checked => onSchedulerPermissionChange(checked, 'write')}
										/>
										<PermissionCheckbox
											id='scheduler-viewReporting'
											label='View Reporting'
											checked={editingGroup.featurePermissions?.scheduler?.viewReporting || false}
											onCheckedChange={checked => onSchedulerPermissionChange(checked, 'viewReporting')}
										/>
									</div>
								</FeatureSection>
							) : null}

							<FeatureSection title='Surveys'>
								{SURVEY_TYPES.map(({ key, label }) => (
									<div key={key} className={css(styles.surveyTypeRow)}>
										<div className={css(styles.surveyTypeName)}>{label}</div>
										<div className={css(styles.permissionCheckboxGroup)}>
											<PermissionCheckbox
												id={`${key}-read`}
												label='Read'
												checked={
													editingGroup.featurePermissions?.survey?.[key]?.read ??
													editingGroup.featurePermissions?.survey?.read ??
													false
												}
												onCheckedChange={checked => onSurveyPermissionChange(checked, 'read', key)}
											/>
											<PermissionCheckbox
												id={`${key}-write`}
												label='Write'
												checked={
													editingGroup.featurePermissions?.survey?.[key]?.write ??
													editingGroup.featurePermissions?.survey?.write ??
													false
												}
												onCheckedChange={checked => onSurveyPermissionChange(checked, 'write', key)}
											/>
											<PermissionCheckbox
												id={`${key}-viewReporting`}
												label='View Reporting'
												checked={
													editingGroup.featurePermissions?.survey?.[key]?.viewReporting ??
													editingGroup.featurePermissions?.survey?.viewReporting ??
													false
												}
												onCheckedChange={checked => onSurveyPermissionChange(checked, 'viewReporting', key)}
											/>
										</div>
									</div>
								))}
							</FeatureSection>

							<FeatureSection title='Content Library'>
								<div className={css(styles.permissionCheckboxGroup)}>
									<PermissionCheckbox
										id='content-library-email'
										label='Email Templates'
										checked={editingGroup.featurePermissions?.systemTemplates?.viewEmailTemplates || false}
										onCheckedChange={checked => onContentLibraryPermissionChange(checked, 'viewEmailTemplates')}
									/>
									<PermissionCheckbox
										id='content-library-social'
										label='Social Templates'
										checked={editingGroup.featurePermissions?.systemTemplates?.viewSocialTemplates || false}
										onCheckedChange={checked => onContentLibraryPermissionChange(checked, 'viewSocialTemplates')}
									/>
									<PermissionCheckbox
										id='content-library-card'
										label='Card Templates'
										checked={editingGroup.featurePermissions?.systemTemplates?.viewCardTemplates || false}
										onCheckedChange={checked => onContentLibraryPermissionChange(checked, 'viewCardTemplates')}
									/>
									<PermissionCheckbox
										id='content-library-html-newsletter'
										label='HTML Newsletter Templates'
										checked={editingGroup.featurePermissions?.systemTemplates?.viewHtmlNewsletterTemplates || false}
										onCheckedChange={checked =>
											onContentLibraryPermissionChange(checked, 'viewHtmlNewsletterTemplates')
										}
									/>
								</div>
							</FeatureSection>

							{!editingGroup.isPredefined && (
								<FeatureSection title='Group Members'>
									<MultiEmployeeSelector employees={selectedUsers} onEmployeesChange={onUserSelectionChange} />
								</FeatureSection>
							)}

							<div className={css(styles.modalActions)}>
								<Button size='sm' onClick={onSaveGroup} disabled={isPending}>
									{isPending ? 'Saving...' : editingGroup.id ? 'Update' : 'Create'}
								</Button>
								<Button variant='secondary' size='sm' onClick={onClose} disabled={isPending}>
									Cancel
								</Button>
							</div>
						</>
					)}
				</div>
			</div>
		</Modal>
	);
};
